/**
 * Copyright © 2025 Delicious AI, LLC
 */

import { LazyQueryHookOptions } from '@apollo/client'
import { useEffect } from 'react'

import {
  GET_TENANT_STORES_SIMPLE,
  GetTenantStoresSimpleQuery,
  GetTenantStoresSimpleQueryVariables,
  Ordering,
} from '@/graphql'

import { useDebouncedItemQuery } from './useDebouncedItemQuery'

type HookQueryVariables = {
  externalStoreId?: string
  searchTerm?: string
  storeId?: string
  isActive?: boolean
  onlyMyStores?: boolean
}

export const useGetTenantStores = (
  variables: HookQueryVariables,
  queryOnMount?: boolean,
  options?: LazyQueryHookOptions<
    GetTenantStoresSimpleQuery,
    GetTenantStoresSimpleQueryVariables
  >,
) => {
  const TenantStoresLazyQuery = useDebouncedItemQuery<
    GetTenantStoresSimpleQuery,
    GetTenantStoresSimpleQueryVariables
  >({
    queryStr: GET_TENANT_STORES_SIMPLE,
    options: {
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      ...options,
    },
    queryOnMount,
  })

  const stores =
    TenantStoresLazyQuery.lazyQuery.meta.data?.tenantStores?.results || []
  useEffect(() => {
    TenantStoresLazyQuery.lazyQuery.query({
      variables: {
        order: { name: Ordering.ASC }, // MAKE A VARIABLE WHEN NEEDED
        pagination: { limit: 300, offset: 0 },
        filters: {
          onlyMyStores: variables.onlyMyStores,
          id: variables.storeId,
          isActive: variables.isActive,
          ...(variables.externalStoreId
            ? { externalStoreId: { inList: [variables.externalStoreId] } }
            : {}),
          AND: {
            location_Name: { iContains: variables.searchTerm },
            OR: {
              location_State: { iContains: variables.searchTerm },
              OR: {
                location_City: { iContains: variables.searchTerm },
                OR: {
                  location_Address1: { iContains: variables.searchTerm },
                },
              },
            },
          },
        },
      },
    })
  }, [JSON.stringify(variables)])

  return {
    state: {
      TenantStoresLazyQuery,
      loading: TenantStoresLazyQuery.lazyQuery.meta.loading,
    },
    stores,
  }
}
